/**
 * Live Help Button Component
 * Floating button that opens the LLM-powered live help modal
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { useGeminiLive } from '../../hooks/useGeminiLive';
import GeminiLiveModal from '../help/GeminiLiveModal';
import { GEMINI_DEMO_SYSTEM_INSTRUCTION } from '../../constants/systemInstructions';
import { useDemoSessionState } from '../../demo/demoSessionStore';

/** Demo Live Help sessions auto-disconnect after 5 minutes (plan §5.2). */
const DEMO_LIVE_HELP_MAX_SECONDS = 5 * 60;

/**
 * Demo token fetcher: password-gated `/api/demo/live-help/token`. Imported
 * lazily so the demo API client stays out of the portal's initial bundle.
 */
const demoTokenFetcher = async () => {
  const { getLiveHelpToken } = await import('../../demo/services/demoService');
  return getLiveHelpToken();
};

/**
 * Live Help Button props
 */
export interface LiveHelpButtonProps {
  position?: 'bottom-right' | 'bottom-left' | 'inline';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
  /**
   * `demo` (on /demo/*): hidden until the demo password gate is passed, uses
   * the password-gated token endpoint, the demo system instruction, example
   * phrases and a 5-minute session cap.
   */
  context?: 'portal' | 'demo';
}

/**
 * Live Help Button Component
 */
export const LiveHelpButton: React.FC<LiveHelpButtonProps> = ({
  position = 'bottom-right',
  size = 'md',
  showLabel = false,
  className = '',
  context = 'portal',
}) => {
  const { t } = useTranslation();
  const { theme, isFeatureEnabled } = useThemeContext();
  const { isRTL } = useLanguageContext();
  const isDemo = context === 'demo';
  const demoState = useDemoSessionState();

  // Use the new Gemini Live hook
  const geminiLive = useGeminiLive(
    isDemo
      ? { systemInstruction: GEMINI_DEMO_SYSTEM_INSTRUCTION, tokenFetcher: demoTokenFetcher }
      : {}
  );
  const { status, connect, disconnect } = geminiLive;
  
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Stable identity (the modal's countdown effect depends on onClose). Declared
  // before the early returns below to respect the Rules of Hooks.
  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    disconnect();
  }, [disconnect]);

  const demoHints = isDemo
    ? [
        t('demo.liveHelp.hint1', '¿Dónde subo la tarjeta de vacunas?'),
        t('demo.liveHelp.hint2', 'Kijan pou m voye kat vaksen an?'),
        t('demo.liveHelp.hint3', "Translate 'your child needs a second MMR dose' to Vietnamese"),
      ]
    : undefined;

  // Don't render if live help is disabled
  if (!isFeatureEnabled('showLiveHelp')) {
    return null;
  }

  // In the demo, Live Help only exists behind the password gate.
  if (isDemo && !demoState.session) {
    return null;
  }

  const sizeClasses = {
    sm: 'w-12 h-12 text-sm',
    md: 'w-14 h-14 text-base',
    lg: 'w-16 h-16 text-lg min-h-[64px]', // Kiosk-friendly
  };

  const positionClasses = {
    'bottom-right': 'fixed bottom-6 right-6 z-50',
    'bottom-left': 'fixed bottom-6 left-6 z-50',
    'inline': 'relative',
  };

  // Open the modal first so "Connecting..." is visible while the mic prompt,
  // token request and WebSocket handshake run. `connect()` must be called
  // synchronously in this handler (no `await` before it): it unlocks the
  // AudioContexts and requests the mic inside the user gesture (Safari).
  const handleClick = () => {
    setIsModalOpen(true);
    if (status === 'IDLE' || status === 'ERROR') {
      void connect();
    }
  };


  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleClick}
        className={`
          ${positionClasses[position]}
          ${sizeClasses[size]}
          ${showLabel ? 'px-4' : ''}
          flex items-center justify-center
          rounded-full shadow-lg
          transition-all duration-200
          hover:scale-110 hover:shadow-xl
          focus:outline-none focus:ring-4 focus:ring-offset-2
          ${className}
          ${isRTL && showLabel ? 'flex-row-reverse' : ''}
        `}
        style={{
          backgroundColor: theme?.colors.primary,
          color: '#FFFFFF',
        }}
        data-testid="live-help-button"
        aria-label={t('help.liveAssistance')}
        title={t('help.liveAssistance')}
      >
        {status === 'CONNECTING' ? (
          <LoadingSpinner className="w-6 h-6 animate-spin" />
        ) : (
          <HeadsetIcon className="w-6 h-6" />
        )}
        {showLabel && (
          <span className={`font-medium ${isRTL ? 'mr-2' : 'ml-2'}`}>
            {t('help.getHelp')}
          </span>
        )}
        
        {/* Pulse indicator when connected */}
        {(status === 'LISTENING' || status === 'SPEAKING') && (
          <span className="absolute top-0 right-0 block h-3 w-3">
            <span 
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ backgroundColor: theme?.colors.success }}
            />
            <span 
              className="relative inline-flex rounded-full h-3 w-3"
              style={{ backgroundColor: theme?.colors.success }}
            />
          </span>
        )}
      </button>

      {/* Help Modal */}
      {isModalOpen && (
        <GeminiLiveModal
          isOpen={isModalOpen}
          onClose={handleClose}
          live={geminiLive}
          hints={demoHints}
          maxSessionSeconds={isDemo ? DEMO_LIVE_HELP_MAX_SECONDS : undefined}
        />
      )}
    </>
  );
};

/**
 * Icon Components
 */
const HeadsetIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" style={style}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export default LiveHelpButton;
